"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { MessageSquareDiff } from "lucide-react"
import { usePathname } from "next/navigation"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { API_ROUTES, ROUTES } from "@/routes"

const feedbackSchema = z.object({
  message: z
    .string()
    .min(10, "Message must be at least 10 characters")
    .max(1000, "Message must be at most 1000 characters"),
  type: z.enum(["BUG", "FEATURE_REQUEST", "OTHER"]),
})

type FeedbackForm = z.infer<typeof feedbackSchema>

export function Feedback() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FeedbackForm>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      message: "",
      type: "OTHER",
    },
  })
  const pathname = usePathname()
  const isShown = pathname.endsWith(ROUTES.merchant) || pathname.endsWith(ROUTES.customer)

  const onSubmit = async (data: FeedbackForm) => {
    try {
      const response = await fetch(API_ROUTES.feedback, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        toast({
          title: "Failed to submit feedback",
          description: "Please try again later",
          variant: "destructive",
        })
      } else {
        toast({
          title: "Feedback submitted",
          description: "Thank you for your feedback!",
        })
      }
    } catch (error) {
      toast({
        title: "Failed to submit feedback",
        description: "Please try again later",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="icon" className={cn("fixed bottom-4 right-4 size-14 rounded-full", !isShown && "hidden")}>
          <MessageSquareDiff style={{ width: 30, height: 30 }} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Feedback Type</label>
            <Select onValueChange={(value) => setValue("type", value as FeedbackForm["type"])} defaultValue="OTHER">
              <SelectTrigger>
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BUG">Bug Report</SelectItem>
                <SelectItem value="FEATURE_REQUEST">Feature Request</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.type && <p className="mt-1 text-sm text-red-500">{errors.type.message}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Message</label>
            <Textarea placeholder="Describe your feedback here..." {...register("message")} />
            {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>}
          </div>

          <Button type="submit" className="w-full">
            Submit Feedback
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
